import React, { useState } from "react";
import {
  Row,
  Col,
  Typography,
  Button,
  Card,
  Carousel,
  Image,
} from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

// Import local assets
import mainEventImg from "../../assets/bloom-main.jpg";
import heroesCupImg from "../../assets/heroes-cup.jpg";
import openEventsImg from "../../assets/focus.jpg";
import jiuJitsuImg from "../../assets/jui-news-image.png";

const { Title, Paragraph, Text } = Typography;

// --- SLIDER + NEWS DATA ---
const sliderData = [
  {
    id: "bloom-day",
    title: "Bloom Day: Yalla Nellab Announces a New Initiative for Special Children",
    description:
      "Supported by volunteers and local partners, Bloom Day reflects Yalla Nellab's ongoing commitment to making sports accessible to everyone.",
    img: mainEventImg,
    path: "/news/bloom-day"
  },
  {
    id: "heroes-day",
    title: "Heroes Cup: tournament dedicated to honoring laborers",
    description:
      "This initiative celebrates the strength, teamwork and spirit of the people who build our communities.",
    img: heroesCupImg,
    path: "/news/heroes-day"
  },
  {
    id: "open-events",
    title: "Open Events - community games for everyone to join and play",
    description:
      "Experience the joy of sports, make new connections and be part of a growing community that plays for passion, not competition.",
    img: openEventsImg,
    path: "/news/open-events"
  },
  {
    id: "jiu-jitsu",
    title: "Jiu-Jitsu Gaining Popularity",
    description:
      "Promoted by the vision and support of the UAE Royal Family, Jiu-Jitsu is rapidly gaining popularity across our community.",
    img: jiuJitsuImg,
    path: "/news/jiu-jitsu"
  },
];

const newsData = sliderData; // using same data for demo

// --- COMPONENT ---
const NewsPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();
  let carouselRef = React.createRef();

  const onChange = (current) => setCurrentSlide(current);

  const handleReadMore = (path) => {
    navigate(path);
  };

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <div style={{ background: "#fff", padding: "50px 0" }}>
      {/* Headline Slider Section */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 16px" }}>
        <Text
          strong
          style={{
            textTransform: "uppercase",
            color: "#6f42c1",
            fontSize: 13,
          }}
        >
          Headlines
        </Text>

        <Carousel
          ref={(ref) => (carouselRef = ref)}
          autoplay
          autoplaySpeed={3000}
          dots={false}
          afterChange={onChange}
          effect="fade"
        >
          {sliderData.map((item, index) => (
            <div key={index}>
              <Row gutter={[32, 32]} align="middle">
                {/* Left Text */}
                <Col xs={24} md={12}>
                  <Title level={4} style={{ marginBottom: 16 }}>
                    {item.title}
                  </Title>
                  <Paragraph
                    style={{
                      fontSize: 16,
                      color: "#555",
                      marginBottom: 24,
                    }}
                  >
                    {item.description}
                  </Paragraph>
                  <Button 
                    type="link" 
                    style={{ padding: 0 }}
                    onClick={() => handleReadMore(item.path)}
                  >
                    Read More →
                  </Button>
                </Col>

                {/* Right Image */}
                <Col xs={24} md={12}>
                  <div
                    style={{
                      width: "100%",
                      height: 400,
                      overflow: "hidden",
                      borderRadius: 8,
                    }}
                  >
                    <Image
                      src={item.img}
                      alt={item.title}
                      preview={false}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          ))}
        </Carousel>

        {/* Pagination Buttons */}
        <div
          style={{
            marginTop: 16,
            textAlign: "center",
          }}
        >
          {sliderData.map((_, i) => (
            <Button
              key={i}
              type="default"
              size="small"
              shape="circle"
              onClick={() => carouselRef.goTo(i)}
              style={{
                margin: "0 4px",
                width: 28,
                height: 28,
                borderRadius: "50%",
                border:
                  currentSlide === i ? "2px solid #6f42c1" : "1px solid #ccc",
                background: currentSlide === i ? "#6f42c1" : "#fff",
                color: currentSlide === i ? "#fff" : "#6f42c1",
                fontWeight: 500,
              }}
            >
              {i + 1}
            </Button>
          ))}
        </div>
      </div>

      {/* News Section */}
      <div
        style={{
          maxWidth: 1200,
          margin: "60px auto 0",
          padding: "0 16px",
        }}
      >
        <Title level={3}>News</Title>
        <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
          The Latest News and Features of Yalla Nellab
        </Text>

        {/* Responsive equal-height grid */}
        <Row gutter={[24, 24]} style={{ display: "flex", flexWrap: "wrap" }}>
          {newsData.map((item, index) => (
            <Col
              xs={24}
              sm={12}
              md={12}
              lg={6}
              key={index}
              style={{ display: "flex" }}
            >
              <Card
                hoverable
                bordered={false}
                style={{
                  borderRadius: 8,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  width: "100%",
                  height: "100%",
                  cursor: "pointer",
                }}
                cover={
                  <img
                    alt={item.title}
                    src={item.img}
                    style={{
                      height: 180,
                      objectFit: "cover",
                      width: "100%",
                    }}
                  />
                }
                onClick={() => handleCardClick(item.path)}
              >
                <div style={{ flexGrow: 1 }}>
                  <Title level={5} style={{ minHeight: 56 }}>
                    {item.title}
                  </Title>
                  <Paragraph
                    ellipsis={{ rows: 2 }}
                    style={{
                      color: "#555",
                      marginBottom: 16,
                      minHeight: 48,
                    }}
                  >
                    {item.description}
                  </Paragraph>
                </div>
                <Button 
                  type="link" 
                  style={{ padding: 0 }}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click event
                    handleReadMore(item.path);
                  }}
                >
                  Read More →
                </Button>
              </Card>
            </Col>
          ))}
        </Row>

        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Button>Load More</Button>
        </div>
      </div>
    </div>
  );
};

export default NewsPage;